import React, { useState } from 'react';
import { 
  Box, Paper, Typography, List, ListItem, ListItemText, Chip, Divider, 
  Grid, FormControl, InputLabel, Select, MenuItem, Button, Switch, FormControlLabel,
  TextField, Card, CardContent
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import SettingsIcon from '@mui/icons-material/Settings';
import FilterListIcon from '@mui/icons-material/FilterList';

import { recentAlerts } from '../data/mockData';

const Alerts = () => {
  const [severityFilter, setSeverityFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [slackNotifications, setSlackNotifications] = useState(false);
  
  // Get unique alert types for filter dropdown
  const alertTypes = ['all', ...new Set(recentAlerts.map(alert => alert.type))];
  
  // Alert configurations (for demo purposes)
  const alertConfigs = [
    { 
      id: 1, 
      name: 'New AI Service Detection', 
      description: 'Alert when a new AI service is detected in your network', 
      enabled: true,
      severity: 'medium'
    },
    { 
      id: 2, 
      name: 'Unusual Usage Pattern', 
      description: 'Alert when there is an unusual spike in AI service usage', 
      enabled: true,
      severity: 'low'
    },
    { 
      id: 3, 
      name: 'Potential Data Sharing', 
      description: 'Alert when large volumes of data are potentially shared with AI services', 
      enabled: true,
      severity: 'high'
    },
    { 
      id: 4, 
      name: 'After-hours Usage', 
      description: 'Alert when AI services are used outside of regular business hours', 
      enabled: false,
      severity: 'medium'
    },
  ];
  
  // Filter alerts based on selections
  const getFilteredAlerts = () => {
    let filtered = [...recentAlerts];
    
    if (severityFilter !== 'all') {
      filtered = filtered.filter(alert => alert.severity === severityFilter);
    }
    
    if (typeFilter !== 'all') {
      filtered = filtered.filter(alert => alert.type === typeFilter);
    }
    
    return filtered;
  };
  
  // Handle severity filter change
  const handleSeverityChange = (e) => {
    setSeverityFilter(e.target.value);
  };
  
  // Handle type filter change
  const handleTypeChange = (e) => {
    setTypeFilter(e.target.value);
  };
  
  // Handle notification toggles
  const handleEmailToggle = () => {
    setEmailNotifications(!emailNotifications);
  };
  
  const handleSlackToggle = () => {
    setSlackNotifications(!slackNotifications);
  };
  
  // Render severity chip with appropriate color
  const renderSeverityChip = (severity) => {
    let color = 'default';
    
    switch (severity) {
      case 'high':
        color = 'error';
        break;
      case 'medium':
        color = 'warning';
        break;
      case 'low':
        color = 'info';
        break;
      default:
        color = 'default';
    }
    
    return (
      <Chip 
        label={severity.charAt(0).toUpperCase() + severity.slice(1)} 
        color={color} 
        size="small" 
        sx={{ ml: 1 }}
      />
    );
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Alerts & Notifications
      </Typography>
      
      <Grid container spacing={3}>
        {/* Alert Settings */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, bgcolor: 'background.paper' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <SettingsIcon sx={{ mr: 1 }} />
              <Typography variant="h6">
                Alert Settings
              </Typography>
            </Box>
            
            <Typography variant="subtitle2" gutterBottom>
              Notification Channels
            </Typography>
            
            <Box sx={{ mb: 3 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={emailNotifications}
                    onChange={handleEmailToggle}
                    color="primary"
                  />
                }
                label="Email Notifications"
              />
              
              {emailNotifications && (
                <TextField
                  fullWidth
                  size="small"
                  label="Notification Email"
                  variant="outlined"
                  defaultValue="admin@example.com"
                  sx={{ mt: 1, mb: 2 }}
                />
              )}
              
              <FormControlLabel
                control={
                  <Switch
                    checked={slackNotifications}
                    onChange={handleSlackToggle}
                    color="primary"
                  />
                }
                label="Slack Notifications"
              />
              
              {slackNotifications && (
                <TextField
                  fullWidth
                  size="small"
                  label="Slack Webhook URL"
                  variant="outlined"
                  defaultValue="https://hooks.slack.com/services/..."
                  sx={{ mt: 1 }}
                />
              )}
            </Box>
            
            <Divider sx={{ my: 2 }} />
            
            <Typography variant="subtitle2" gutterBottom>
              Alert Configurations
            </Typography>
            
            {alertConfigs.map((config) => (
              <Card key={config.id} variant="outlined" sx={{ mb: 2, bgcolor: 'background.default' }}>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle1">
                      {config.name}
                    </Typography>
                    {renderSeverityChip(config.severity)}
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {config.description}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={config.enabled}
                          color="primary"
                        />
                      }
                      label={config.enabled ? "Enabled" : "Disabled"}
                    />
                    
                    <Button size="small" variant="outlined">
                      Edit
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            ))}
            
            <Button variant="contained" fullWidth sx={{ mt: 2 }}>
              Add New Alert Rule
            </Button>
          </Paper>
        </Grid>
        
        {/* Recent Alerts */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2, bgcolor: 'background.paper' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <NotificationsIcon sx={{ mr: 1 }} />
              <Typography variant="h6" sx={{ flexGrow: 1 }}>
                Recent Alerts
              </Typography>
              
              <Button
                size="small"
                startIcon={<FilterListIcon />}
                variant="outlined"
                sx={{ mr: 1 }}
              >
                Filters
              </Button>
            </Box>
            
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel id="severity-filter-label">Severity</InputLabel>
                  <Select
                    labelId="severity-filter-label"
                    id="severity-filter"
                    value={severityFilter}
                    label="Severity"
                    onChange={handleSeverityChange}
                  >
                    <MenuItem value="all">All Severities</MenuItem>
                    <MenuItem value="high">High</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="low">Low</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel id="type-filter-label">Alert Type</InputLabel>
                  <Select
                    labelId="type-filter-label"
                    id="type-filter"
                    value={typeFilter}
                    label="Alert Type"
                    onChange={handleTypeChange}
                  >
                    {alertTypes.map((type) => (
                      <MenuItem key={type} value={type}>
                        {type === 'all' ? 'All Types' : type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            
            <List>
              {getFilteredAlerts().length > 0 ? (
                getFilteredAlerts().map((alert) => (
                  <React.Fragment key={alert.id}>
                    <ListItem sx={{ px: 2, py: 1 }}>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant="subtitle1">{alert.type}</Typography>
                            {renderSeverityChip(alert.severity)}
                          </Box>
                        }
                        secondary={
                          <>
                            <Typography variant="body2" color="text.secondary">
                              {new Date(alert.timestamp).toLocaleString()}
                            </Typography>
                            <Typography variant="body2">
                              {alert.message}
                            </Typography>
                          </>
                        }
                      />
                      <Button size="small" variant="outlined">
                        View Details
                      </Button>
                    </ListItem>
                    <Divider component="li" />
                  </React.Fragment>
                ))
              ) : (
                <ListItem>
                  <ListItemText 
                    primary="No alerts match your filters" 
                    secondary="Try changing your filter criteria to see more results" 
                  />
                </ListItem>
              )}
            </List>
            
            {getFilteredAlerts().length > 0 && (
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
                <Button variant="outlined">
                  Mark All as Read
                </Button>
                <Button variant="outlined">
                  View All Alerts
                </Button>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Alerts;